import type { ChartRootProps } from '@/shared/ui/chart'

export type MultiMetricFrequency = 'date' | 'week' | 'month_of_year' | 'year'

export interface MultiMetricChartSeries {
  field: string
  label: string
  color: string
  current: Readonly<Record<string, number>>
  previous?: Readonly<Record<string, number>>
  forecast?: Readonly<Record<string, number>>
  formatValue: (value: number | null | undefined) => string
  formatAxisValue?: (value: number) => string
}

export interface MultiMetricChartInput {
  series: readonly MultiMetricChartSeries[]
  frequency: MultiMetricFrequency
  previousPeriodLabel: string
  forecastLabel: string
  formatDate: (value: string | number | Date) => string
}

export type MultiMetricChartOptions = NonNullable<ChartRootProps['options']>

function numeric(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function points(values: Readonly<Record<string, number>>): [number, number][] {
  return Object.entries(values)
    .map(([date, value]) => [new Date(date).getTime(), numeric(value)] as [number, number])
    .filter(([date]) => Number.isFinite(date))
    .sort((left, right) => left[0] - right[0])
}

function interval(frequency: MultiMetricFrequency): number {
  return 86_400_000 * ({ date: 1, week: 7, month_of_year: 30, year: 365 }[frequency] ?? 1)
}

function escapeTooltipText(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

/**
 * Pixel gap nudged between two metrics' main lines when their values coincide exactly (e.g. a
 * "Purchase" key event mirroring GA4's primary conversion 1:1) and would otherwise render as one
 * indistinguishable stroke. Small enough to read as a rendering artifact rather than a data change.
 */
const LINE_SEPARATION_PX = 1.5

interface SeparableGraphic {
  translateY?: number
  attr: (attrs: Record<string, number>) => unknown
}
interface SeparableSeries {
  group?: SeparableGraphic
  markerGroup?: SeparableGraphic
  dataLabelsGroup?: SeparableGraphic
  userOptions?: { custom?: { metricField?: string; compare?: boolean; forecast?: boolean } }
}
interface SeparableChart {
  series: SeparableSeries[]
}

function isMainMetricLine(series: SeparableSeries): boolean {
  const custom = series.userOptions?.custom
  return Boolean(custom?.metricField) && !custom?.compare && !custom?.forecast
}

/** Alternates each successive line above/below its true position: 0, +1.5, -1.5, +3, -3, … */
function separationOffset(order: number): number {
  if (order === 0) return 0
  const magnitude = Math.ceil(order / 2) * LINE_SEPARATION_PX
  return order % 2 === 1 ? magnitude : -magnitude
}

/**
 * Nudges each main metric line's rendered graphics apart by a constant pixel amount so identical
 * (or momentarily crossing) series stay visually distinguishable — a render-time pixel offset
 * rather than a data change, so tooltips and axis values remain exact.
 */
function separateOverlappingLines(chart: SeparableChart): void {
  chart.series.filter(isMainMetricLine).forEach((series, order) => {
    const offset = separationOffset(order)
    if (offset === 0) return
    for (const graphic of [series.group, series.markerGroup, series.dataLabelsGroup]) {
      if (graphic && typeof graphic.translateY === 'number') {
        graphic.attr({ translateY: graphic.translateY + offset })
      }
    }
  })
}

/** Module-08-owned adapter from a neutral multi-metric spec to ChartRoot's opaque options. */
export function buildMultiMetricChartOptions(
  input: MultiMetricChartInput,
): MultiMetricChartOptions {
  const yAxis: Array<Record<string, unknown>> = []
  const chartSeries: Array<Record<string, unknown>> = []

  input.series.forEach((metric, index) => {
    yAxis.push({
      title: { text: metric.label },
      gridLineWidth: index === 0 ? 1 : 0,
      labels: {
        style: { fontSize: '11px' },
        formatter() {
          const value = Number((this as unknown as { value?: unknown }).value)
          return metric.formatAxisValue?.(value) ?? String(value)
        },
      },
      visible: input.series.length < 3,
      opposite: index > 0,
      endOnTick: false,
      min: 0,
    })
    chartSeries.push({
      type: 'spline',
      name: metric.label,
      color: metric.color,
      yAxis: index,
      data: points(metric.current),
      custom: { metricField: metric.field },
    })

    const previous = points(metric.previous ?? {})
    const current = points(metric.current)
    if (previous.length) {
      chartSeries.push({
        type: 'spline',
        name: `${metric.label} · ${input.previousPeriodLabel}`,
        color: metric.color,
        yAxis: index,
        dashStyle: 'Dash',
        opacity: 0.4,
        showInLegend: false,
        data: current.map(([date], pointIndex) => [date, previous[pointIndex]?.[1] ?? 0]),
        custom: { metricField: metric.field, compare: true },
      })
    }

    if (metric.forecast && Object.keys(metric.forecast).length) {
      chartSeries.push({
        type: 'spline',
        name: `${metric.label} · ${input.forecastLabel}`,
        color: metric.color,
        yAxis: index,
        dashStyle: 'ShortDash',
        opacity: 0.7,
        data: points(metric.forecast),
        custom: { metricField: metric.field, forecast: true },
      })
    }
  })

  return {
    chart: {
      type: 'spline',
      marginBottom: 110,
      events: {
        render() {
          separateOverlappingLines(this as unknown as SeparableChart)
        },
      },
    },
    title: { text: '' },
    xAxis: {
      type: 'datetime',
      tickInterval: interval(input.frequency),
      labels: {
        rotation: input.frequency === 'week' ? 0 : -45,
        align: input.frequency === 'week' ? 'center' : 'right',
        style: {
          color: 'var(--ui-color-fg)',
          fontFamily: 'Arial, sans-serif',
          fontSize: '12px',
          fontWeight: '200',
          padding: '10px',
        },
        // Native SVG labels preserve the same typography and avoid Highcharts producing
        // `rotate(-45 NaN NaN)` on a valid one-point dataset (and logging a browser error).
        useHTML: false,
      },
    },
    yAxis,
    legend: {
      itemStyle: {
        whiteSpace: 'nowrap',
        font: '10pt Trebuchet MS, Verdana, sans-serif',
        color: 'black',
      },
      useHTML: false,
      symbolWidth: 20,
    },
    tooltip: {
      shared: true,
      formatter() {
        const context = this as unknown as {
          x: number
          points?: Array<{
            color?: string
            y?: number | null
            series: {
              color?: string
              name: string
              userOptions: { custom?: { metricField?: string } }
            }
          }>
        }
        const rows = (context.points ?? []).map((point) => {
          const metric = input.series.find(
            (candidate) => candidate.field === point.series.userOptions.custom?.metricField,
          )
          const value = metric?.formatValue(point.y) ?? String(point.y ?? '-')
          const color = point.color ?? point.series.color ?? 'var(--ui-color-muted)'
          return [
            '<div class="ui-chart-tooltip__row grid grid-cols-[0.5rem_minmax(0,1fr)_auto] items-center gap-xs">',
            `<span class="ui-chart-tooltip__dot h-xs w-xs rounded-full" style="background:${escapeTooltipText(color)}"></span>`,
            `<span class="ui-chart-tooltip__label min-w-0 truncate">${escapeTooltipText(point.series.name)}</span>`,
            `<strong class="ui-chart-tooltip__value font-numeric tabular-nums">${escapeTooltipText(value)}</strong>`,
            '</div>',
          ].join('')
        })
        return [
          '<div class="ui-chart-tooltip grid min-w-52 gap-xs rounded-md border border-border bg-surface p-sm font-sans text-xs text-fg shadow-pop">',
          `<div class="ui-chart-tooltip__title border-b border-border pb-2xs font-bold">${escapeTooltipText(input.formatDate(context.x))}</div>`,
          ...rows,
          '</div>',
        ].join('')
      },
    },
    plotOptions: {
      series: { pointPlacement: 'on', lineWidth: 2, marker: { enabled: false, radius: 3 } },
    },
    credits: { enabled: false },
    series: chartSeries,
  } as MultiMetricChartOptions
}
